import { styles } from './allocationsEdit.styles'
import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FilterType,
  FormSkeleton,
  useNotify
} from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  List,
  ListItem,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumBloqueoCapacidad, EnumDispatchType, EnumOperationState } from '../../api'
import Page, { PageLayout } from '../../components/page/page'
import AllocationEditForm from './edit/allocation.edit'
import { AllocationModel, OperationModel } from './allocationsEdit.model'
import {
  distributeAllocations,
  filterAllocationApplicableActionsConfiguration,
  filterAllocationApplicableSecondaryActionsConfiguration,
  getOperation,
  replicateAllocations,
  saveAllocations,
  updateLockState
} from './allocationsEdit.service'
import {
  changeActionIsLoading,
  generateIconsFromConfiguration
} from '../../helpers/dataTableActionIconHelper'

import { ActionIcons, NonAuthRoutes } from '../../model'

import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons'
import PageHeader from '../../components/page/pageHeader'
import PageLeftColumn from '../../components/page/pageLeftColumn'
import PageRightColumn from '../../components/page/pageRightColumn'
import PageCenterColumn from '../../components/page/pageCenterColumn'
import { formatLocaleNumber, formatLocalePercentage } from '../../helpers/numberHelper'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { useReferredState } from '../../hooks/useReferredState'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { useHttpError } from '../../hooks/useHttpError'
import { getUserPermissions } from '../../helpers/permissionsHelper'
import { Pages } from '../../model/RBAC/constants'
import { useErrorDetailListDialog } from '../../hooks'
import moment from 'moment'
import { ErrorDetailList } from '../../components/errorDetailList/errorDetailList'
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import { DateTime } from 'luxon'
import { getCustomerDomain, notifyError } from '../../redux'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

export interface AllocationsEditPageProps {}

const AllocationsEditPage: React.FC<any> = props => {
  let history = useHistory()
  const [t] = useTranslation([
    'globalerisLibrary',
    'translation',
    'allocationsEdit',
    'enums',
    'confirmDialogs'
  ])
  const { info } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [operationId] = React.useState<number | undefined>(props.location.state.id)
  const [operation, operationRef, setOperation] = useReferredState<OperationModel | undefined>(
    undefined
  )
  const [originalOperation, setOriginalOperation] = React.useState<OperationModel | undefined>(
    undefined
  )

  const [dispatchType] = React.useState<EnumDispatchType | undefined>(
    props.location.state.dispatchType
  )
  const [pendingChanges, setPendingChanges] = React.useState(false)
  const [activeAllocation, setActiveAllocation] = React.useState<AllocationModel | null>(null)
  const [formDialogOpen, setFormDialogOpen] = React.useState<boolean>(false)
  const [totalAllocationsQuantity, setTotalAllocationsQuantity] = React.useState<number>(0)
  const [originalNominationQuantity, setOriginalNominationQuantity] = React.useState<number>(0)
  const [initReplicateDate, setInitReplicateDate] = React.useState<any>(undefined)
  const [endReplicateDate, setEndReplicateDate] = React.useState<any>(undefined)
  const [operationToReplicate, setOperationToReplicate] = React.useState<
    OperationModel | undefined
  >(undefined)
  const [showReplicateForm, setShowReplicateForm] = React.useState<boolean>(false)

  const [isFormWaiting, setIsFormWaiting] = React.useState<boolean>(false)

  //#region Hooks

  const { showError, clearError, errorProps } = useHttpError()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  useEffect(() => {
    const fetchOperation = async (id: number) => {
      setOriginalOperation(await getOperationItem(id))
    }

    if (operationId) {
      fetchOperation(operationId)
    } else {
      history.goBack()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (originalOperation !== undefined) {
      setOperation(originalOperation)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalOperation])

  useEffect(() => {
    const calculateTotalQuantityDifference = () => {
      if (operation) {
        return (
          operation!.allocations.reduce((a, b) => a + (b['quantity'] || 0), 0) -
          originalNominationQuantity!
        )
      }
      return 0
    }

    setTotalAllocationsQuantity(calculateTotalQuantityDifference())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation?.allocations])

  //#endregion

  //#region Handlers

  const onCancelHandler = () => {
    history.goBack()
  }

  const onDiscardChangesHandler = () => {
    setOperation(originalOperation)
    setPendingChanges(false)
  }

  const onSaveHandler = async () => {
    if (operation && operation.allocations) {
      const modifiedAllocations = operation.allocations.filter(x => x.isModified === true)

      if (modifiedAllocations.length === 0) {
        info(t('allocationsEdit:notifications.info.noPendingChanges'))
        return
      }

      setIsFormWaiting(true)

      const operationToDistribute = structuredClone(operation)
      // proposed FIX to be added later
      // const operationToDistribute = structuredClone({
      //   ...operation,
      //   allocations: operation.allocations.map(({ actions, secondaryActions, ...rest }) => rest)
      // }) // Remove actions from the object to avoid problems with structuredClone() - cannot clone react Elements

      operationToDistribute.allocations = operationToDistribute.allocations.map(
        (allocation: AllocationModel) => {
          if (allocation.isModified) {
            return { ...allocation, locked: EnumBloqueoCapacidad.BloqueadoUsuario }
          }
          return allocation
        }
      )

      await saveAllocations(operationToDistribute).then(response => {
        if (response.success) {
          if (response.data !== null) {
            info(t('allocationsEdit:notifications.success.save'))
            history.push(NonAuthRoutes.allocations)
          }
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('allocationsEdit:notifications.error.save'),
                [response.error],
                moment(operationToDistribute.operationDate).format('DD/MM/YYYY')
              )
            : notifyError(t('httpErrors:api.subtitle'))
        }
        setIsFormWaiting(false)
      })
    }
  }

  const onAllocateHandler = async () => {
    if (operation && operation.allocations) {
      const modifiedAllocations = operation.allocations.filter(x => x.isModified === true)

      if (modifiedAllocations.length === 0) {
        info(t('allocationsEdit:notifications.info.noPendingChanges'))
        return
      } else if (
        operation.allocations.filter(
          x => x.isModified === true || x.locked !== EnumBloqueoCapacidad.Libre
        ).length === operation.allocations.length
      ) {
        info(t('allocationsEdit:notifications.info.noFreeAllocations'))
        return
      }

      setIsFormWaiting(true)

      const operationToDistribute = structuredClone(operation)

      operationToDistribute.allocations = operationToDistribute.allocations.map(
        (allocation: AllocationModel) => {
          if (allocation.isModified) {
            return { ...allocation, locked: EnumBloqueoCapacidad.BloqueadoUsuario }
          }
          return allocation
        }
      )

      await distributeAllocations(operationToDistribute).then(async response => {
        if (response.success) {
          info(t('allocationsEdit:notifications.success.distribute'))
          if (response.data !== null && response.data.length > 0) {
            setOperation({
              ...operation,
              allocations: operation.allocations.map(allocation => {
                const modifiedAllocation = response.data.find(x => x.id === allocation.id)
                return {
                  ...allocation,
                  actions: generateDataTableActions(allocation),
                  quantity: modifiedAllocation?.quantity,
                  allocatedQuantityBreakdown: `${formatLocaleNumber(
                    modifiedAllocation?.quantity,
                    DecimalScale.ENERGY
                  )} (${formatLocaleNumber(
                    modifiedAllocation?.usePercentage,
                    DecimalScale.DEFAULT
                  )}%)`,
                  usePercentage: modifiedAllocation?.usePercentage
                }
              })
            })
          }
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('allocationsEdit:notifications.error.distributeAllocations'),
                [response.error],
                moment(operationToDistribute.operationDate).format('DD/MM/yyyy')
              )
            : notifyError(t('httpErrors:api.subtitle'))
        }
        setIsFormWaiting(false)
      })
    }
  }

  const onAllocateReplicateHandler = async () => {
    if (operation && operation.allocations) {
      const modifiedAllocations = operation.allocations.filter(x => x.isModified === true)

      if (modifiedAllocations.length === 0) {
        info(t('allocationsEdit:notifications.info.noPendingChanges'))
        return
      } else if (
        operation.allocations.filter(
          x => x.isModified === true || x.locked !== EnumBloqueoCapacidad.Libre
        ).length === operation.allocations.length
      ) {
        info(t('allocationsEdit:notifications.info.noFreeAllocations'))
        return
      }

      setIsFormWaiting(true)

      const operationToReplicate = structuredClone(operation)
      operationToReplicate.allocations = []

      operation.allocations.forEach((allocation: AllocationModel) => {
        if (allocation.isModified) {
          operationToReplicate.allocations.push({
            ...allocation,
            locked: EnumBloqueoCapacidad.BloqueadoUsuario
          })
        }
      })

      var operationDate = moment(operation.operationDate)
      var operationDateUtc = DateTime.utc(
        operationDate.year(),
        operationDate.month() + 1,
        operationDate.date()
      )

      setInitReplicateDate(new Date(operationDateUtc))
      setEndReplicateDate(new Date(operationDateUtc))
      setOperationToReplicate(operationToReplicate)

      setShowReplicateForm(true)
    }
  }

  const onReplicateAllocationsHandler = async () => {
    if (operation && operationToReplicate) {
      setIsFormWaiting(true)
      await replicateAllocations(
        operation.allocations,
        operationToReplicate.allocations,
        initReplicateDate,
        endReplicateDate!
      ).then(async response => {
        if (response.success) {
          if (response.data !== null && response.data.length > 0) {
            setOperation({
              ...operation,
              allocations: operation.allocations.map(allocation => {
                const modifiedAllocation = response.data.find(x => x.id === allocation.id)
                allocation.isModified = false
                allocation.locked = modifiedAllocation.lock
                return {
                  ...allocation,
                  actions: generateDataTableActions(allocation),
                  quantity: modifiedAllocation?.quantity,
                  allocatedQuantityBreakdown: `${formatLocaleNumber(
                    modifiedAllocation?.quantity,
                    DecimalScale.ENERGY
                  )} (${formatLocaleNumber(
                    modifiedAllocation?.usePercentage,
                    DecimalScale.DEFAULT
                  )}%)`,
                  usePercentage: modifiedAllocation?.usePercentage
                }
              })
            })
          }
          info(t('allocationsEdit:notifications.success.replicate'))
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('allocationsEdit:notifications.error.replicate'),
                [response.error],
                moment(operation.operationDate).format('DD/MM/YYYY')
              )
            : notifyError(t('httpErrors:api.subtitle'))
        }
        setIsFormWaiting(false)
        setShowReplicateForm(false)
        setPendingChanges(false)
      })
    }
  }

  const onLockActionHandler = async (item: AllocationModel) => {
    getConfirmation(ConfirmationDialogs.LOCK).then(async () => {
      await changeProductCapacityLockState(
        item,
        EnumBloqueoCapacidad.BloqueadoUsuario,
        ActionIcons.UNLOCK
      )
      closeConfirmation()
    })
  }

  const onUnlockActionHandler = async (item: AllocationModel) => {
    getConfirmation(ConfirmationDialogs.UNLOCK).then(async () => {
      await changeProductCapacityLockState(item, EnumBloqueoCapacidad.Libre, ActionIcons.LOCK)
      closeConfirmation()
    })
  }

  const onEditCancelHandler = () => {
    setActiveAllocation(null)
    setFormDialogOpen(false)
  }

  const onEditSaveHandler = (modifiedAllocation: AllocationModel) => {
    const index = operation?.allocations.findIndex(x => x.id === modifiedAllocation.id)

    let allocations = [
      ...operation!.allocations.slice(0, index),
      {
        ...modifiedAllocation,
        actions: generateDataTableActions({ ...modifiedAllocation, isModified: true }),
        isModified: true,
        allocatedQuantityBreakdown: `${formatLocaleNumber(
          modifiedAllocation?.quantity!,
          DecimalScale.ENERGY
        )} (${formatLocalePercentage(
          modifiedAllocation?.quantity! / modifiedAllocation.productQdHired!
        )}%)`
      },
      ...operation!.allocations.slice(index! + 1)
    ]

    setOperation({
      ...operation!,
      state: EnumOperationState.PendienteRenvio,
      quantity: allocations.reduce((a, b) => a + (b['quantity'] || 0), 0),
      allocations: allocations
    })

    setPendingChanges(true)
    setActiveAllocation(null)
    setFormDialogOpen(false)
  }

  //#endregion

  //#region Form / DataTable configuration

  const dataTableColumns = React.useMemo((): DataTableColumn[] => {
    return [
      {
        name: 'actions',
        display: true,
        label: ' ',
        type: DataType.HTML,
        columnWidth: 30
      },
      {
        name: 'productCapacityBreakdown',
        display: true,
        label: t('allocationsEdit:dataTable.columns.productCapacity')
      },
      {
        name: 'secondaryActions',
        display: true,
        label: ' ',
        type: DataType.HTML,
        columnWidth: 1
      },
      {
        name: 'capacity',
        display: true,
        label: t('allocationsEdit:dataTable.columns.capacity'),
        type: DataType.NUMBER,
        decimalScale: DecimalScale.ENERGY
      },
      {
        name: 'allocatedQuantityBreakdown',
        display: true,
        label: t('allocationsEdit:dataTable.columns.allocatedQuantity'),
        type: DataType.TEXT
      },
      {
        name: 'lastSendInformationBreakdown',
        display: true,
        label: t('allocationsEdit:dataTable.columns.lastSend'),
        type: DataType.TEXT,
        hideForResponsive: true
      },
      {
        name: 'TSOInformationBreakdown',
        display: true,
        label: t('allocationsEdit:dataTable.columns.TSOInformation'),
        type: DataType.TEXT,
        hideForResponsive: true
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const datatableOptions = React.useMemo((): DataTableOptions => {
    return {
      i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
      search: false,
      elevation: 1,
      filterType: FilterType.NONE,
      download: false,
      print: false,
      canEdit: true,
      canInsert: false,
      canDelete: false,
      selectableRows: false,
      customInsertEditModal: true,
      onRowEdit: async (row: AllocationModel) => {
        if (row.locked !== EnumBloqueoCapacidad.Libre) {
          info(
            t('allocationsEdit:notifications.info.locked', {
              code: row.productCapacityBreakdown
            })
          )
          return false
        }
        setActiveAllocation({ ...row })
        setFormDialogOpen(true)
        return true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAllocation, formDialogOpen])

  //#endregion

  //#region Functions

  const changeProductCapacityLockState = async (
    allocation: AllocationModel,
    lock: EnumBloqueoCapacidad,
    icon: ActionIcons
  ) => {
    if (allocation.locked !== undefined && allocation.productCapacityId !== undefined) {
      updateDataTableRowData({
        ...allocation,
        actionsConfiguration: changeActionIsLoading(allocation.actionsConfiguration, icon, true)
      })

      await updateLockState(allocation.productCapacityId!, lock).then(success => {
        if (success) {
          updateDataTableRowData({
            ...allocation,
            locked: lock,
            actionsConfiguration: changeActionIsLoading(
              allocation.actionsConfiguration,
              icon,
              false
            )
          })
          info(t('allocationsEdit:notifications.success.updateLockState'))
        }
      })
    }
  }

  const updateDataTableRowData = (row: AllocationModel) => {
    if (operationRef.current !== undefined) {
      setOperation({
        ...operationRef.current,
        allocations: operationRef.current.allocations.map(allocation => {
          return allocation.id === row.id
            ? { ...row, actions: generateDataTableActions(row) }
            : { ...allocation }
        })
      })
    }
  }

  const generateDataTableActions = (item: AllocationModel): JSX.Element => {
    if (item.actionsConfiguration && item.actionsConfiguration.length > 0) {
      return generateIconsFromConfiguration(
        item,
        filterAllocationApplicableActionsConfiguration(item, item.actionsConfiguration),
        new Map<ActionIcons, Function | undefined>([
          [ActionIcons.LOCK, onUnlockActionHandler],
          [ActionIcons.UNLOCK, onLockActionHandler],
          [ActionIcons.LOCK_DISABLED, undefined],
          [ActionIcons.LOCK_TEMPORARY, undefined]
        ])
      )
    } else {
      return <></>
    }
  }

  const generateDataTableSecondaryActions = (item: AllocationModel): JSX.Element => {
    if (item.secondaryActionsConfiguration && item.secondaryActionsConfiguration.length > 0) {
      return generateIconsFromConfiguration(
        item,
        filterAllocationApplicableSecondaryActionsConfiguration(
          item,
          item.secondaryActionsConfiguration
        ),
        new Map<ActionIcons, Function>([])
      )
    } else {
      return <></>
    }
  }

  const AllocationsAdditionalInformation = () => {
    return (
      <>
        <Typography display="inline" color="primary" variant="subtitle1" component="span">
          {formatLocaleNumber(originalNominationQuantity, DecimalScale.ENERGY)} kWh
        </Typography>
        <Typography display="inline" color="secondary" variant="subtitle2" component="span">
          {totalAllocationsQuantity > 0 && (
            <>
              <ArrowDropUp></ArrowDropUp>
              {formatLocaleNumber(totalAllocationsQuantity, DecimalScale.ENERGY)} kWh
            </>
          )}
          {totalAllocationsQuantity < 0 && (
            <>
              <ArrowDropDown></ArrowDropDown>
              {formatLocaleNumber(totalAllocationsQuantity, DecimalScale.ENERGY)} kWh
            </>
          )}
        </Typography>
      </>
    )
  }

  const NominationAdditionalInformation = () => {
    return (
      <>
        {operation && originalNominationQuantity !== operation.quantity && (
          <Typography display="inline" color="secondary" variant="subtitle2" component="span">
            {formatLocaleNumber(originalNominationQuantity, DecimalScale.ENERGY)} kWh
            <FontAwesomeIcon
              icon={faAngleDoubleRight as IconProp}
              style={{ marginLeft: currentTheme.spacing(1), marginRight: currentTheme.spacing(1) }}
            ></FontAwesomeIcon>
          </Typography>
        )}
        <Typography display="inline" color="primary" variant="subtitle1" component="span">
          {formatLocaleNumber(operation?.quantity, DecimalScale.ENERGY)} kWh
        </Typography>
      </>
    )
  }

  const AdditionalInformation = () => {
    if (operation !== undefined) {
      if (dispatchType === EnumDispatchType.Reparto02G) {
        return AllocationsAdditionalInformation()
      } else {
        return NominationAdditionalInformation()
      }
    }
  }

  const getOperationItem = async (id: number): Promise<OperationModel> => {
    return await getOperation(id).then(response => {
      if (response.success) {
        clearError()
        setOriginalNominationQuantity(response.data.quantity)
        return {
          ...response.data,
          allocations: response.data.allocations.map((item: AllocationModel) => {
            return {
              ...item,
              actions: generateDataTableActions(item),
              secondaryActions: generateDataTableSecondaryActions(item)
            }
          })
        }
      } else {
        showError({
          title: t('allocationsEdit:title'),
          errorCode: response.error!.code,
          // actions: [
          //   {
          //     label: t('allocationsEdit:mainActions.back'),
          //     onClick: () => {
          //       history.goBack()
          //     },
          //     default: true
          //   }
          // ],
          onRefresh: async () => setOperation(await getOperationItem(id))
        })

        return undefined
      }
    })
  }

  //#endregion

  return (
    <Page layout={PageLayout.MULTI_COLUMN} error={errorProps}>
      <PageLeftColumn>
        {operation ? (
          <>
            <Typography variant="h1" color="primary" noWrap={true}>
              {`${operation?.quantityFormatted} kWh`}
            </Typography>
            <List className={classes.resume}>
              <ListItem disableGutters>
                <Typography variant={'h2'}>{operation?.operationDateFormatted}</Typography>
              </ListItem>
              <ListItem disableGutters>
                <Typography variant={'body2'}>{operation?.typeDescription}</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'body2'}>{operation?.logisticElementDescription}</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>Estado</Typography>
                <Typography variant={'subtitle2'}>{operation?.stateDescription}</Typography>
              </ListItem>
              <ListItem divider disableGutters>
                <Typography variant={'caption'}>Precio</Typography>
                <Typography variant={'subtitle2'}>{operation?.priceFormatted} €/kWh</Typography>
              </ListItem>
            </List>
          </>
        ) : (
          <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={9}></FormSkeleton>
        )}
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('allocationsEdit:dataTable.title')}>
          <Button onClick={onCancelHandler} size="small" color="primary">
            {t('allocationsEdit:mainActions.cancel')}
          </Button>
          {dispatchType === EnumDispatchType.Reparto02G ? (
            <Button
              onClick={onAllocateHandler}
              size="small"
              color="primary"
              disabled={!pendingChanges || isFormWaiting}
            >
              {t('allocationsEdit:mainActions.allocate')}
            </Button>
          ) : null}
          <Button
            onClick={onDiscardChangesHandler}
            size="small"
            color="primary"
            disabled={!pendingChanges || isFormWaiting}
          >
            {t('allocationsEdit:mainActions.discardChanges')}
          </Button>
          {dispatchType === EnumDispatchType.Reparto02G ? (
            <Button
              onClick={onAllocateReplicateHandler}
              size="small"
              color="primary"
              disabled={!pendingChanges || isFormWaiting}
            >
              {t('allocationsEdit:mainActions.replicate')}
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={onSaveHandler}
            size="small"
            color="primary"
            disabled={!pendingChanges || isFormWaiting}
          >
            {t('allocationsEdit:mainActions.save')}
          </Button>
        </PageHeader>
        <DataTable
          title={AdditionalInformation()}
          data={operation?.allocations}
          columns={dataTableColumns}
          options={{
            ...datatableOptions,
            analytics: {
              customer: getCustomerDomain(),
              view: t('allocations:dataTable.analytics')
            }
          }}
          userPermissions={getUserPermissions(Pages.AllocationsEdit)}
        />
      </PageCenterColumn>
      <PageRightColumn></PageRightColumn>

      {activeAllocation !== null && (
        <AllocationEditForm
          data={activeAllocation}
          open={formDialogOpen}
          onSave={onEditSaveHandler}
          onCancel={onEditCancelHandler}
        ></AllocationEditForm>
      )}

      <Dialog fullWidth disableBackdropClick disableEscapeKeyDown open={showReplicateForm}>
        <DialogContent>
          <Typography variant="h1" color="secondary">
            {t('allocationsEdit:mainActions.replicate')}
          </Typography>
          <div className={classes.ReplicateDates}>
            <Grid container>
              <Grid item xs={12} sm={6} className={classes.InitReplicateDate}>
                <MuiPickersUtilsProvider utils={MomentUtils} locale="es">
                  <KeyboardDatePicker
                    openTo="date"
                    views={['date']}
                    inputVariant="outlined"
                    label={t('allocationsEdit:forms.editAllocation.actions.initDate')}
                    format="DD/MM/YYYY"
                    InputAdornmentProps={{ position: 'end' }}
                    autoOk={true}
                    okLabel=""
                    cancelLabel=""
                    value={initReplicateDate}
                    onChange={setInitReplicateDate}
                    minDate={
                      new Date(
                        DateTime.utc(
                          moment(initReplicateDate).year(),
                          moment(initReplicateDate).month() + 1,
                          moment(initReplicateDate).date()
                        )
                      )
                    }
                    disabled={true}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={12} sm={6} className={classes.EndReplicateDate}>
                <MuiPickersUtilsProvider utils={MomentUtils} locale="es">
                  <KeyboardDatePicker
                    openTo="date"
                    views={['date']}
                    inputVariant="outlined"
                    label={t('allocationsEdit:forms.editAllocation.actions.endDate')}
                    format="DD/MM/YYYY"
                    InputAdornmentProps={{ position: 'end' }}
                    autoOk={true}
                    okLabel=""
                    cancelLabel=""
                    value={endReplicateDate}
                    onChange={setEndReplicateDate}
                    minDate={
                      new Date(
                        DateTime.utc(
                          moment(initReplicateDate).year(),
                          moment(initReplicateDate).month() + 1,
                          moment(initReplicateDate).date()
                        )
                      )
                    }
                    maxDate={
                      new Date(
                        DateTime.utc(
                          moment(initReplicateDate).year(),
                          moment(initReplicateDate).month() + 2,
                          moment(initReplicateDate).date()
                        )
                      )
                    }
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setShowReplicateForm(false)}>
            {t('allocationsEdit:mainActions.cancel')}
          </Button>
          <Button size="small" onClick={onReplicateAllocationsHandler} color="primary">
            {t('allocationsEdit:mainActions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
    </Page>
  )
}

export default AllocationsEditPage
